import { differenceInYears, isValid, parse } from 'date-fns';

/** Date-of-birth formats accepted from source CSV exports. */
export const DOB_FORMATS = ['MM-dd-yyyy', 'MM/dd/yyyy', 'yyyy-MM-dd'] as const;

/** Ages above this are treated as unrealistic (likely a mapping/format error). */
export const MAX_REALISTIC_AGE_YEARS = 120;

export interface DobValidationResult {
  isValid: boolean;
  /** Parsed date when the value could be parsed, even if otherwise invalid. */
  date?: Date;
  /** Whole-year age relative to `now`, when computable. */
  ageYears?: number;
  errorMessage?: string;
}

/**
 * Parse a DOB string using the accepted CSV formats. Returns `undefined` when
 * the value cannot be parsed by any supported format.
 */
export function parseDobDate(value: string): Date | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  for (const fmt of DOB_FORMATS) {
    const parsed = parse(trimmed, fmt, new Date());
    if (isValid(parsed)) return parsed;
  }
  return undefined;
}

/**
 * Validate a date-of-birth value and guard against the QA issue where bad DOB
 * data produced negative or unrealistic student ages.
 *
 * Empty values are treated as valid here (presence is enforced separately by the
 * required-field rules), so this only fails on genuinely bad dates.
 */
export function validateDob(
  value?: string,
  now: Date = new Date(),
): DobValidationResult {
  if (!value || value.trim() === '') {
    return { isValid: true };
  }

  const date = parseDobDate(value);
  if (!date) {
    return {
      isValid: false,
      errorMessage: 'Invalid date of birth. Use MM-DD-YYYY (e.g. 03-14-2015).',
    };
  }

  if (date.getTime() > now.getTime()) {
    return {
      isValid: false,
      date,
      errorMessage: 'Date of birth cannot be in the future.',
    };
  }

  const ageYears = differenceInYears(now, date);
  if (ageYears < 0 || ageYears > MAX_REALISTIC_AGE_YEARS) {
    return {
      isValid: false,
      date,
      ageYears,
      errorMessage: `Date of birth produces an unrealistic age (${ageYears}). Check the column mapping and date format.`,
    };
  }

  return { isValid: true, date, ageYears };
}

/**
 * Validate a class age range (start/end). Guards against negative or inverted
 * ranges that crash downstream age filters.
 */
export function validateAgeRange(
  startYear?: number,
  startMonth?: number,
  endYear?: number,
  endMonth?: number,
): { isValid: boolean; errorMessage?: string } {
  const startMonths = (startYear ?? 0) * 12 + (startMonth ?? 0);
  const endMonths = (endYear ?? 0) * 12 + (endMonth ?? 0);

  if (startMonths < 0 || endMonths < 0) {
    return { isValid: false, errorMessage: 'Age values cannot be negative.' };
  }
  // Only flag inversion when an end age was actually provided.
  if ((endYear != null || endMonth != null) && endMonths < startMonths) {
    return {
      isValid: false,
      errorMessage: 'End age cannot be lower than start age.',
    };
  }
  return { isValid: true };
}
